import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../api/client';
import { useAuth } from '../context/AuthContext.jsx';
import { useWorkspace } from '../context/WorkspaceContext.jsx';
import { Badge, Empty, STATUS_TONE, PRIORITY_TONE, fmtDate, daysUntil, label, Skeleton } from '../components/ui.jsx';
import { useToast, useConfirm } from '../components/feedback.jsx';
import { t } from '../i18n/index.jsx';
import { cachedFetch } from '../api/cache.js';

function StatIcon({ label }) {
  const common = { width: 18, height: 18, stroke: '#64748b', strokeWidth: 2.2 };
  if (label === 'Companies') return <svg {...common} viewBox="0 0 24 24" fill="none"><path d="M3 21V7l7-4v18"/><path d="M21 21V11l-6-4v14"/><path d="M3 21h18"/></svg>;
  if (label === 'Active projects') return <svg {...common} viewBox="0 0 24 24" fill="none"><path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/></svg>;
  if (label === 'Open contracts') return <svg {...common} viewBox="0 0 24 24" fill="none"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><path d="M14 2v6h6"/><path d="M16 13H8M16 17H8M10 9H8"/></svg>;
  if (label === 'Active funds') return <svg {...common} viewBox="0 0 24 24" fill="none"><path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg>;
  return <svg {...common} viewBox="0 0 24 24" fill="none"><path d="M8 6h13M8 12h13M8 18h13M3 6h.01M3 12h.01M3 18h.01"/></svg>;
}

function CountryBars({ rows }) {
  if (!rows?.length) return <Empty>{t('No projects yet.')}</Empty>;
  const max = Math.max(...rows.map((r) => Number(r.n)), 1);
  return (
    <div className="country-bars">
      {rows.map((r) => (
        <div key={r.country} className="country-bar-row">
          <span className="country-bar-label">{r.country}</span>
          <div className="country-bar-track">
            <div className="country-bar-fill" style={{ width: `${(Number(r.n) / max) * 100}%` }} />
          </div>
          <span className="country-bar-count">{r.n}</span>
        </div>
      ))}
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const { workspace, isFunds, isTrade, isGeneral } = useWorkspace();
  const toast = useToast();
  const confirm = useConfirm();
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const [pending, setPending] = useState([]);
  const [restorePoints, setRestorePoints] = useState([]);

  async function loadDashboard() {
    try {
      const path = `/dashboard?workspace=${workspace}`;
      const res = await cachedFetch(path, () => api.get(path), { force: true, ttl: 30_000 });
      setData(res);
    } catch (e) {
      setErr(e.message);
    }
  }

  async function loadAiData() {
    try {
      const p = await api.get('/ai/actions');
      setPending(p.data || []);
      const r = await api.get('/ai/restore-points');
      setRestorePoints(r.data || []);
    } catch { /* AI optional */ }
  }

  useEffect(() => {
    setData(null);
    loadDashboard();
    loadAiData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspace]);

  const overdueCount = data?.counts?.overdue_tasks ?? 0;

  async function handleApprove(id) {
    try {
      await api.post(`/ai/actions/${id}/approve`);
      await Promise.all([loadAiData(), loadDashboard()]);
      toast(t('Action approved'));
    } catch (e) {
      toast(e.message, { tone: 'error' });
    }
  }

  async function handleReject(id) {
    try {
      await api.post(`/ai/actions/${id}/reject`);
      await loadAiData();
      toast(t('Action rejected'));
    } catch (e) {
      toast(e.message, { tone: 'error' });
    }
  }

  async function handleUndo(rp) {
    const ok = await confirm({
      title: t('Undo AI changes'),
      message: t('Undo "{label}"? This will revert {n} change(s) made by the assistant.', { label: rp.label, n: rp.action_count }),
      confirmLabel: t('Undo'), danger: true,
    });
    if (!ok) return;
    try {
      await api.post(`/ai/restore-points/${rp.id}/undo`);
      await Promise.all([loadAiData(), loadDashboard()]);
      toast(t('Changes reverted'));
    } catch (e) {
      toast(e.message, { tone: 'error' });
    }
  }

  if (err) return <div className="alert error">{err}</div>;
  if (!data) {
    return (
      <div className="page fade-in dashboard-page">
        <div className="dash-hero">
          <div>
            <Skeleton width={160} height={28} />
            <div style={{ marginTop: 10 }}><Skeleton width={220} height={14} /></div>
          </div>
        </div>
        <div className="stat-grid cols-3" style={{ marginBottom: 18 }}>
          {[0, 1, 2].map((i) => (
            <div key={i} className="stat-card"><Skeleton width="40%" height={28} /><div style={{ marginTop: 10 }}><Skeleton width="60%" height={12} /></div></div>
          ))}
        </div>
        <div className="stat-grid">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="stat-card"><Skeleton width="36%" height={28} /><div style={{ marginTop: 10 }}><Skeleton width="55%" height={12} /></div></div>
          ))}
        </div>
      </div>
    );
  }

  const c = data.counts;
  const fundsCards = [
    { key: 'Active funds', label: t('Active funds'), value: c.active_funds ?? 0, to: '/funds' },
    { key: 'Fund clients', label: t('Fund clients'), value: c.fund_clients ?? 0, to: '/funds' },
    { key: 'Grant approved', label: t('Grants approved'), value: `€${Number(data.grant_approved || 0).toLocaleString()}`, to: '/funds' },
  ];
  const tradeCards = [
    { key: 'Companies', label: t('Companies'), value: c.companies, to: '/companies' },
    { key: 'Active projects', label: t('Active projects'), value: c.active_projects, to: '/projects' },
    { key: 'Open contracts', label: t('Open contracts'), value: c.open_contracts, to: '/contracts' },
    { key: 'Open tasks', label: t('Open tasks'), value: c.open_tasks, to: '/tasks' },
  ];
  const generalCards = [
    { key: 'Active projects', label: t('Active initiatives'), value: c.active_projects, to: '/projects' },
    { key: 'Open tasks', label: t('Open tasks'), value: c.open_tasks, to: '/tasks' },
    { key: 'Companies', label: t('Companies'), value: c.companies, to: '/companies' },
  ];

  return (
    <div className="page fade-in dashboard-page">
      <div className="dash-hero">
        <div>
          <h1 className="page-title" style={{ margin: 0 }}>{t('Dashboard')}</h1>
          <p className="muted sm" style={{ margin: '6px 0 0' }}>
            {t('Welcome back, {name}', { name: user?.name })}
            {' · '}
            <strong>{isFunds ? t('Italy — Funds') : isGeneral ? t('General') : t('Morocco — Trade')}</strong>
          </p>
        </div>
        <div className="dash-quick-links">
          {isFunds && <Link to="/funds" className="btn ghost sm">{t('Funds')}</Link>}
          {isTrade && <Link to="/projects" className="btn ghost sm">{t('Projects')}</Link>}
          {isGeneral && <Link to="/projects" className="btn ghost sm">{t('Initiatives')}</Link>}
          <Link to="/tasks" className="btn ghost sm">{t('Tasks')}</Link>
          <Link to="/calendar" className="btn ghost sm">{t('Calendar')}</Link>
          <Link to="/assistant" className="btn primary sm">{t('Assistant')}</Link>
        </div>
      </div>

      {overdueCount > 0 && (
        <div className="dash-alert">
          <strong>{overdueCount > 1 ? t('{n} overdue tasks', { n: overdueCount }) : t('1 overdue task')}</strong>
          <span className="muted sm"> — {t('review them on the Tasks page')}</span>
          <Link to="/tasks" className="btn danger-solid sm" style={{ marginLeft: 'auto' }}>{t('View tasks')}</Link>
        </div>
      )}

      {pending.length > 0 && (
        <div className="dash-alert dash-alert-warn">
          <strong>{pending.length > 1 ? t('{n} assistant actions awaiting your approval', { n: pending.length }) : t('1 assistant action awaiting your approval')}</strong>
          <Link to="/assistant" className="btn primary sm" style={{ marginLeft: 'auto' }}>{t('Review')}</Link>
        </div>
      )}

      {user.must_setup_2fa && (
        <div className="dash-alert dash-alert-warn">
          <strong>{t('Two-factor authentication is required for your role')}</strong>
          <span className="muted sm"> — {t('set it up on your Account page')}</span>
          <Link to="/account" className="btn primary sm" style={{ marginLeft: 'auto' }}>{t('Set up now')}</Link>
        </div>
      )}

      {isFunds && (
        <>
          <div className="section-label">{t('Italy — Funds')}</div>
          <div className="stat-grid cols-3">
            {fundsCards.map((card) => (
              <Link key={card.key} to={card.to} className="stat-card">
                <div style={{ marginBottom: 8, opacity: .65 }}><StatIcon label={card.key} /></div>
                <div className="stat-value">{card.value}</div>
                <div className="stat-label">{card.label}</div>
              </Link>
            ))}
          </div>

          <div className="dash-cols">
            <section className="panel">
              <h2 className="panel-title">{t('Payment milestones due (30 days)')}</h2>
              {(!data.upcoming_sal || data.upcoming_sal.length === 0) ? (
                <Empty>{t('No payment milestones due soon.')}</Empty>
              ) : (
                <ul className="line-list">
                  {data.upcoming_sal.map((s) => {
                    const d = daysUntil(s.planned_date);
                    return (
                      <li key={s.id}>
                        <div className="ll-main">
                          <Link className="ll-title" to={`/fund-clients/${s.fund_client_id}`}>SAL {s.seq} · {s.title}</Link>
                          <span className="muted sm">{s.client_name} · {s.fund_name}</span>
                        </div>
                        <div className="ll-meta">
                          <Badge tone={STATUS_TONE[s.status] || 'gray'}>{label(s.status)}</Badge>
                          <span className={`due ${d < 0 ? 'overdue' : d <= 2 ? 'soon' : ''}`}>
                            {d < 0 ? t('{n}d overdue', { n: -d }) : d === 0 ? t('today') : t('in {n}d', { n: d })}
                          </span>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </section>

            <section className="panel">
              <div className="panel-head">
                <h2 className="panel-title">{t('Tasks due soon')}</h2>
                <Link to="/tasks" className="btn ghost sm">{t('All tasks')}</Link>
              </div>
              {data.upcoming_tasks.length === 0 ? (
                <Empty>{t('Nothing due in the next 7 days')}</Empty>
              ) : (
                <ul className="line-list">
                  {data.upcoming_tasks.map((task) => {
                    const d = daysUntil(task.due_date);
                    return (
                      <li key={task.id}>
                        <div className="ll-main">
                          <span className="ll-title">{task.title}</span>
                          <span className="muted sm">{task.project_name || task.fund_client_name || '—'}</span>
                        </div>
                        <div className="ll-meta">
                          <Badge tone={PRIORITY_TONE[task.priority]}>{label(task.priority)}</Badge>
                          <span className={`due ${d < 0 ? 'overdue' : d <= 2 ? 'soon' : ''}`}>
                            {d < 0 ? t('{n}d overdue', { n: -d }) : d === 0 ? t('today') : t('in {n}d', { n: d })}
                          </span>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </section>
          </div>
        </>
      )}

      {isTrade && (
        <>
          <div className="section-label">{t('Morocco — Trade')}</div>
          <div className="stat-grid">
            {tradeCards.map((card) => (
              <Link key={card.key} to={card.to} className="stat-card">
                <div style={{ marginBottom: 8, opacity: .65 }}><StatIcon label={card.key} /></div>
                <div className="stat-value">{card.value}</div>
                <div className="stat-label">{card.label}</div>
              </Link>
            ))}
          </div>

          <div className="dash-split">
            <section className="panel">
              <div className="panel-head">
                <h2 className="panel-title">{t('At a glance')}</h2>
                <Link to="/calendar" className="btn ghost sm">{t('Full calendar')}</Link>
              </div>
              <p className="muted sm" style={{ margin: '0 0 12px' }}>
                {t('Key deadlines from tasks and contracts — open the calendar for the full schedule.')}
              </p>
              <ul className="line-list">
                {(data.upcoming_tasks || []).slice(0, 4).map((task) => {
                  const d = daysUntil(task.due_date);
                  return (
                    <li key={`t-${task.id}`}>
                      <div className="ll-main">
                        <Link className="ll-title" to="/tasks">{task.title}</Link>
                        <span className="muted sm">{task.project_name || task.fund_client_name || t('Task')}</span>
                      </div>
                      <div className="ll-meta">
                        <Badge tone={PRIORITY_TONE[task.priority]}>{label(task.priority)}</Badge>
                        <span className={`due ${d != null && d < 0 ? 'overdue' : d != null && d <= 2 ? 'soon' : ''}`}>
                          {d == null ? '—' : d < 0 ? t('{n}d overdue', { n: -d }) : d === 0 ? t('today') : t('in {n}d', { n: d })}
                        </span>
                      </div>
                    </li>
                  );
                })}
                {(data.expiring_contracts || []).slice(0, 3).map((ct) => (
                  <li key={`c-${ct.id}`}>
                    <div className="ll-main">
                      <span className="ll-title">{ct.title}</span>
                      <span className="muted sm">{ct.project_name || t('Contract')}</span>
                    </div>
                    <div className="ll-meta">
                      <Badge tone={STATUS_TONE[ct.status]}>{label(ct.status)}</Badge>
                      <span className="due">{fmtDate(ct.expiry_date)}</span>
                    </div>
                  </li>
                ))}
                {!(data.upcoming_tasks?.length) && !(data.expiring_contracts?.length) && (
                  <li><Empty>{t('Nothing due in the next 7 days')}</Empty></li>
                )}
              </ul>
            </section>

            <section className="panel">
              <h2 className="panel-title">{t('Projects by country')}</h2>
              <CountryBars rows={data.projects_by_country} />
            </section>
          </div>

          <div className="dash-cols">
            <section className="panel">
              <div className="panel-head">
                <h2 className="panel-title">{t('Tasks due soon')}</h2>
                <Link to="/tasks" className="btn ghost sm">{t('All tasks')}</Link>
              </div>
              {data.upcoming_tasks.length === 0 ? (
                <Empty>{t('Nothing due in the next 7 days')}</Empty>
              ) : (
                <ul className="line-list">
                  {data.upcoming_tasks.map((task) => {
                    const d = daysUntil(task.due_date);
                    return (
                      <li key={task.id}>
                        <div className="ll-main">
                          <span className="ll-title">{task.title}</span>
                          <span className="muted sm">{task.project_name || task.fund_client_name || '—'}</span>
                        </div>
                        <div className="ll-meta">
                          <Badge tone={PRIORITY_TONE[task.priority]}>{label(task.priority)}</Badge>
                          <span className={`due ${d < 0 ? 'overdue' : d <= 2 ? 'soon' : ''}`}>
                            {d < 0 ? t('{n}d overdue', { n: -d }) : d === 0 ? t('today') : t('in {n}d', { n: d })}
                          </span>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </section>

            <section className="panel">
              <h2 className="panel-title">{t('Contracts expiring (30 days)')}</h2>
              {data.expiring_contracts.length === 0 ? (
                <Empty>{t('No contracts expiring soon.')}</Empty>
              ) : (
                <ul className="line-list">
                  {data.expiring_contracts.map((ct) => (
                    <li key={ct.id}>
                      <div className="ll-main">
                        <span className="ll-title">{ct.title}</span>
                        <span className="muted sm">{ct.project_name || '—'}</span>
                      </div>
                      <div className="ll-meta">
                        <Badge tone={STATUS_TONE[ct.status]}>{label(ct.status)}</Badge>
                        <span className="due">{fmtDate(ct.expiry_date)}</span>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          </div>
        </>
      )}

      {isGeneral && (
        <>
          <div className="section-label">{t('General')}</div>
          <div className="stat-grid cols-3">
            {generalCards.map((card) => (
              <Link key={card.key} to={card.to} className="stat-card">
                <div style={{ marginBottom: 8, opacity: .65 }}><StatIcon label={card.key} /></div>
                <div className="stat-value">{card.value}</div>
                <div className="stat-label">{card.label}</div>
              </Link>
            ))}
          </div>

          <section className="panel">
            <div className="panel-head">
              <h2 className="panel-title">{t('Tasks due soon')}</h2>
              <Link to="/tasks" className="btn ghost sm">{t('All tasks')}</Link>
            </div>
            {(data.upcoming_tasks || []).length === 0 ? (
              <Empty>{t('Nothing due in the next 7 days')}</Empty>
            ) : (
              <ul className="line-list">
                {data.upcoming_tasks.map((task) => {
                  const d = daysUntil(task.due_date);
                  return (
                    <li key={task.id}>
                      <div className="ll-main">
                        <span className="ll-title">{task.title}</span>
                        <span className="muted sm">{task.project_name || '—'}</span>
                      </div>
                      <div className="ll-meta">
                        <Badge tone={PRIORITY_TONE[task.priority]}>{label(task.priority)}</Badge>
                        <span className={`due ${d < 0 ? 'overdue' : d <= 2 ? 'soon' : ''}`}>
                          {d < 0 ? t('{n}d overdue', { n: -d }) : d === 0 ? t('today') : t('in {n}d', { n: d })}
                        </span>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </section>
        </>
      )}

      {(pending.length > 0 || restorePoints.length > 0) && (
        <>
          <div className="section-label" style={{ marginTop: 8 }}>{t('Assistant activity')}</div>
          <div className="dash-cols">
            {pending.length > 0 && (
              <section className="panel">
                <h2 className="panel-title" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span>{t('Actions awaiting approval')}</span>
                  <Badge tone="amber">{pending.length}</Badge>
                </h2>
                <ul className="line-list">
                  {pending.map((a) => (
                    <li key={a.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div className="ll-main">
                        <span className="ll-title">
                          {a.summary || a.tool}
                          {a.kind === 'destructive' && <Badge tone="red" style={{ marginLeft: 8 }}>{t('deletes data')}</Badge>}
                        </span>
                        <span className="muted sm">{t('Requested')} {fmtDate(a.created_at?.slice?.(0, 10) || a.created_at)}</span>
                      </div>
                      <div className="ll-meta" style={{ flexDirection: 'row', gap: 6 }}>
                        <button type="button" className="btn sm primary" onClick={() => handleApprove(a.id)}>{t('Approve')}</button>
                        <button type="button" className="btn sm ghost" onClick={() => handleReject(a.id)}>{t('Reject')}</button>
                      </div>
                    </li>
                  ))}
                </ul>
              </section>
            )}

            {restorePoints.length > 0 && (
              <section className="panel">
                <h2 className="panel-title">{t('Recent assistant changes (undo)')}</h2>
                <ul className="line-list">
                  {restorePoints.slice(0, 5).map((rp) => (
                    <li key={rp.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div className="ll-main">
                        <span className="ll-title" style={{ textDecoration: rp.undone_at ? 'line-through' : 'none' }}>
                          {rp.label}
                        </span>
                        <span className="muted sm">
                          {rp.action_count > 1 ? t('{n} changes', { n: rp.action_count }) : t('1 change')} · {fmtDate(rp.created_at?.slice?.(0, 10) || rp.created_at)}
                        </span>
                      </div>
                      <div className="ll-meta">
                        {rp.undone_at ? (
                          <span className="muted sm">{t('undone')}</span>
                        ) : (
                          <button type="button" className="btn sm ghost" onClick={() => handleUndo(rp)}>{t('Undo')}</button>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              </section>
            )}
          </div>
        </>
      )}
    </div>
  );
}